import { useEffect, useState } from 'react';
import type { FeedbackRecord } from '@feedback-system/types';
import { fetchFeedback } from '../api';
import { ProjectFilter } from '../components/ProjectFilter';
import { FeedbackCard } from '../components/FeedbackCard';

interface FeedbackListProps {
  onSelectFeedback: (feedbackId: string) => void;
}

export function FeedbackList({ onSelectFeedback }: FeedbackListProps) {
  const [feedbackList, setFeedbackList] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const loadFeedback = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchFeedback(
          projectId || undefined,
          status || undefined,
          severity || undefined,
          page,
          20
        );
        setFeedbackList(response.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, [projectId, status, severity, page]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-dark-100 mb-4">Feedback</h1>
        <ProjectFilter
          projectId={projectId}
          status={status}
          severity={severity}
          onProjectChange={setProjectId}
          onStatusChange={setStatus}
          onSeverityChange={setSeverity}
        />
      </div>

      {error && (
        <div className="card bg-accent-red/10 border border-accent-red/30">
          <p className="text-accent-red text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="card text-center py-12">
          <p className="text-dark-200">Loading feedback...</p>
        </div>
      ) : feedbackList.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-dark-200">No feedback found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedbackList.map((feedback) => (
            <FeedbackCard
              key={feedback.id}
              feedback={feedback}
              onClick={() => onSelectFeedback(feedback.id)}
            />
          ))}
        </div>
      )}

      {feedbackList.length > 0 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="flex items-center text-dark-200 text-sm">Page {page}</span>
          <button onClick={() => setPage(page + 1)} className="btn-secondary">
            Next
          </button>
        </div>
      )}
    </div>
  );
}
